/* @flow */
import escapeRegExp from 'lodash.escaperegexp';
import { List, Set, Map, fromJS } from 'immutable';
import reactStringReplace from 'react-string-replace';
import { sort, join, map, pipe, replace } from 'ramda';
import sanitize from 'sanitize-html';
import { escapeDefangRegex } from './defang.js';
import { INDICATOR_TYPES } from 'trustar/constellation/types.js';

// A sorter function for strings
const longestFirst = (a, b) => {
  const lengthA = a.length;
  const lengthB = b.length;

  if (lengthA === lengthB) {
    return 0;
  } else if (lengthA > lengthB) {
    return -1;
  } else {
    return 1;
  }
};

/* Construct regexes to match indicators */
const addDefangedIndicators = (indicators: List<Map<string, string>>) => {
  return indicators.map(x => {
    switch(x.get('indicatorType')) {
    case 'IP':
    case 'URL':
    case 'EMAIL_ADDRESS':
      return escapeDefangRegex(x.get('value'));
    default:
      return escapeRegExp(x.get('value'));
    }
  });
};

/* Replace literal whitespace with a regex matcher so we can
 * catch 'this that' as well as 'this     that' */
const wordsToRegString = pipe(
  map(replace(/\s+?/, '\\s+?')),
  sort(longestFirst),
  join('|')
);

/**
 * Exported for testing
 */
export const exactMatchReplace = (str: string, re: RegExp, replacer: (a: string) => string): string => {
  return str.replace(re, (fullMatch, exactMatch) => {
    return fullMatch.replace(exactMatch, replacer(exactMatch));
  });
};

export const matchReplaceFullWordsRegular = (str: string, words: string[], replacer: Function) => {
  // Step 1: Build a regex from all words. The (?:\\s|\\b) will match whitespace
  // orword boundaries like periods (\b). So this will not match word
  // substrings, but it will match words surrounded by whitespace or boundaries.
  // I.e. match 'trustar' and 'my_company' in
  //     'extract trustar and my_company. But not trustartech.'
  //  -> 'extract <MATCH> and <MATCH>. But not trustartech.'
  const regex = new RegExp('(?:\\s|\\b)(' + wordsToRegString(words) + ')(?:\\s|\\b)', 'gmi');

  // Step 2: Do the actual replacement with the regex. This step just marks off
  // words in a way that will make them matchable by react string replace.
  // Ultimately the need for two steps comes from the fact that
  // reactStringReplace uses str.split under the hood, and using split with
  // regex will happily strip everything in the regex that's not part of the
  // match group. We don't want to stip the whitespace and boundaries in the
  // original regex, so we replace them with something we DO want stripped and
  // use that in react string replace. These `<@@__@@>` tags are arbitrary. Just
  // something unlikely to appear on any text on its own.
  const _tmp = exactMatchReplace(str, regex, x => `<@@__@@>${x}</@@__@@>`);

  // Step 3: Finally use the intermediate string to run the replacement,
  // stripping out the custom replacement tags we inserted in step 2.
  const result = reactStringReplace(_tmp, /<@@__@@>([\s\S]+?)<\/@@__@@>/gmi, replacer);

  // Store the constructed regex for debugging purposes. Only in dev b/c
  // otherwise we might run into hard to track down memory leaks and it can
  // cause test failures. I.e. jest will find two arrays inequal (deep compare)
  // if we have this _regex prop trailing off the array. However, the test
  // runner doesn't display anything b/c this is just a rogue prop on an array
  if (process.env.NODE_ENV === 'development') {
    result._regex = regex;
  }

  return result;
};

const matchReplaceFullWordsJSON = (str: string, words: string[]) => {
  const regex = new RegExp('(?:\\s|\\b)(' + wordsToRegString(words) + ')(?:\\s|\\b)', 'gmi');
  return exactMatchReplace(str, regex, x => `<span style='color: #60b044;''>${x}</span>`);
};

export const getReportIndicators = (indicators: List<Map<string, string>>): List<Map<string, string>> => {
  const indicatorTypes = fromJS(INDICATOR_TYPES).keySeq();
  return indicators
    .filter(x => indicatorTypes.contains(x.get('indicatorType')));
};

const createMatchers = (indicators: List<Map<string, string>>) =>
  addDefangedIndicators(indicators).toJS();

/* NOTE:
 *
 * There are two paths for matching and extracting IOCs:
 *
 * The traditional path where we match and extract using react-string-replace is the first.
 * The second 'simple' option is used for highlighting a formated JSON structure and
 * replaces matched terms with <span> HTML and returns ordinary strings.
 *
 * Report.js uses one path or the other based on the report in question.
 *
 * The first extraction method is a known performance bottleneck for reports with
 * high numbers of IOCs.
 *
 */

export const matchIOCsRegular = (
  text: string,
  indicators: List<Map<string, string>>,
  replacer: Function) => {
  const matchers = createMatchers(indicators);
  let highlightedText = text;
  if (matchers.length > 0) {
    highlightedText = matchReplaceFullWordsRegular(text, matchers, replacer);
  }
  return highlightedText;
};

export const matchIOCsJSON = (
  text: string,
  indicators: List<Map<string, string>>) => {
  const matchers = createMatchers(indicators);
  let highlightedText = text;
  if (matchers.length > 0) {
    highlightedText = matchReplaceFullWordsJSON(text, matchers);
  }
  return highlightedText;
};

/* Determine if a report body is a valid JSON structure */
export const parseJsonIfPossible = (content: string): Map<string, any> => {
  let parsed;
  try {
    parsed = JSON.parse(content);
    return Map({ valid: true, parsed });
  } catch(e) {
    return Map({ valid: false, parsed });
  }
};

const isTerminalValue = (value) => {
  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean') {
    return true;
  }
};

/* NOTE: We override the default options for sanitize-html for a tags.
 * For a tags, we transform by stringifying the entire tag and attributes. */
const sanitizeOptions = (content: string) => ({
  allowedTags: [ 'h3', 'h4', 'h5', 'h6', 'blockquote', 'p', 'ul', 'ol',
    'nl', 'li', 'b', 'i', 'strong', 'em', 'strike', 'code', 'hr', 'br', 'div',
    'table', 'thead', 'caption', 'tbody', 'tr', 'th', 'td', 'pre' ],
  allowedAttributes: [],
  transformTags: {
    a: (tagName, attributes) => {
      /* sanitize all a tags to plain text */
      return {
        tagName: 'a',
        text: JSON.stringify(content)
      };
    }
  }
});

/* NOTE: Code for Temporary Fraud Workaround:
 * ************************************************************************************ */

/* Arbitrary HTML attribute to identify fraud links */
const arbitraryFraudAttribute = '@@_allowCustomFraudLink';
/* Hardcoded set of fraud URL patterns to whitelisted a tags in report content */
const arbitraryFraudWhitelistPatterns = Set([
  'https://www.google.com/search?q=',
  'https://www.google.com/',
  'https://crunchbase.com/textsearch?q=',
  'https://www.crunchbase.com/',
  'https://www.linkedin.com/search/results/people/?keywords=',
  'https://www.linkedin.com/',
  'https://twitter.com/search?q=',
  'https://www.twitter.com/',
  'https://internal.softlayer.com/'
]);

/* NOTE: Content must contain one of the above URL patterns and the
 * arbitrary fraud attribute to be whitelisted */
const shouldWhitelistForFraud = (content: string): boolean => {
  if (typeof content !== 'string') return false; // safeguard against non-string values, e.g. booleans, or null
  if (!content.includes(arbitraryFraudAttribute)) return false;
  return arbitraryFraudWhitelistPatterns.some(url => content.includes(url));
};

/* ************************************************************************************** */

/* Apply custom defaults to HTML sanitization */
const safelyParseContent = (content: string): string => {
  return shouldWhitelistForFraud(content)
    ? content // if it passes fraud screen, simply provide content directly. Otherwise sanitize:
    : sanitize(content, sanitizeOptions(content));
};

// Nesting threshold;
const threshold = 6;
/* Recursively turn arbitrary JSON structure into nested human-readable HTML ༼ つ ▀̿_▀̿ ༽つ */
export const parseAndSanitizeJsonToHtml = (blob: any, nesting: number = 0, builder: string = ''): string => {
  const identation = nesting * threshold;
  if (isTerminalValue(blob)) {
    builder += `
      <div
        style="margin-left: ${identation}px;">
        ${safelyParseContent(blob)}
      </div>
    `;
  } else if (Array.isArray(blob)) {
    blob.forEach(entry => {
      builder += parseAndSanitizeJsonToHtml(entry, nesting + 1);
    });
  } else if (typeof blob === 'object') {
    for (let key in blob) {
      builder += `
        <div style="color: #528bff; margin-left: ${identation}px; font-weight: bold">
          ${safelyParseContent(key)}:
        </div>
      `;
      builder += parseAndSanitizeJsonToHtml(blob[key], nesting + 1);
    }
  }
  return builder;
};



// WEBPACK FOOTER //
// ./web_modules/trustar/reports/extraction.js